/**
 * Shadow Controls component
 * Sliders for shadow offset, blur, opacity and color
 */

import { Slider, Switch, cn } from '@frameit/ui';
import { useProjectStore, useSelectedArtwork } from '../../stores/projectStore';

export interface ShadowControlsProps {
  className?: string;
}

/** Shadow controls for framed artwork */
export function ShadowControls({ className }: ShadowControlsProps): JSX.Element | null {
  const selectedArtwork = useSelectedArtwork();
  const updateShadow = useProjectStore((s) => s.updateShadow);

  if (!selectedArtwork) return null;

  const shadow = selectedArtwork.shadow;
  const enabled = shadow?.enabled ?? true;
  const offsetX = shadow?.offsetX ?? 5;
  const offsetY = shadow?.offsetY ?? 5;
  const blur = shadow?.blur ?? 15;
  const opacity = shadow?.opacity ?? 0.3;
  const color = shadow?.color ?? '#000000';

  return (
    <div className={cn('space-y-3', className)}>
      {/* Enable toggle */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium">Drop Shadow</span>
        <Switch
          checked={enabled}
          onCheckedChange={(checked) => updateShadow(selectedArtwork.id, { enabled: checked })}
        />
      </div>

      {enabled && (
        <>
          {/* Offset X */}
          <div>
            <label className="text-xs text-muted-foreground">Horizontal Offset</label>
            <Slider
              value={offsetX}
              min={-30}
              max={30}
              step={1}
              onChange={(value) => updateShadow(selectedArtwork.id, { offsetX: value })}
              showValue
              formatValue={(v) => `${v}px`}
            />
          </div>

          {/* Offset Y */}
          <div>
            <label className="text-xs text-muted-foreground">Vertical Offset</label>
            <Slider
              value={offsetY}
              min={-30}
              max={30}
              step={1}
              onChange={(value) => updateShadow(selectedArtwork.id, { offsetY: value })}
              showValue
              formatValue={(v) => `${v}px`}
            />
          </div>

          {/* Blur */}
          <div>
            <label className="text-xs text-muted-foreground">Blur</label>
            <Slider
              value={blur}
              min={0}
              max={50}
              step={1}
              onChange={(value) => updateShadow(selectedArtwork.id, { blur: value })}
              showValue
              formatValue={(v) => `${v}px`}
            />
          </div>

          {/* Opacity */}
          <div>
            <label className="text-xs text-muted-foreground">Opacity</label>
            <Slider
              value={opacity}
              min={0}
              max={1}
              step={0.05}
              onChange={(value) => updateShadow(selectedArtwork.id, { opacity: value })}
              showValue
              formatValue={(v) => `${Math.round(v * 100)}%`}
            />
          </div>

          {/* Color */}
          <div>
            <label className="text-xs text-muted-foreground">Shadow Color</label>
            <div className="flex items-center gap-2 mt-1">
              <input
                type="color"
                value={color}
                onChange={(e) => updateShadow(selectedArtwork.id, { color: e.target.value })}
                className="w-8 h-8 rounded border cursor-pointer"
              />
              <span className="text-xs text-muted-foreground">{color}</span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
